import { saveAs } from 'file-saver';
import { Editor } from 'tiptap';
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  CodeBlock,
  HardBreak,
  Heading,
  Italic,
  Link,
  OrderedList,
  TodoList,
  Strike,
  Underline,
} from 'tiptap-extensions';

import Image from '@/editor/nodes/Image';
import ListItem from '@/editor/nodes/ListItem';
import TodoItem from '@/editor/nodes/TodoItem';

import RecoverChangesModal from '@/components/editor/RecoverChangesModal.vue';

import HTMLExportProvider from '@/providers/export/html.provider';
import MarkdownExportProvider from '@/providers/export/markdown.provider';

const EXPORT_FORMAT_MAP = {
  html: HTMLExportProvider,
  md: MarkdownExportProvider,
};

const state = {
  notes: [],
  current: null,

  params: {},

  editor: new Editor({
    autoFocus: true,
    extensions: [
      new Blockquote(),
      new Bold(),
      new BulletList(),
      new Code(),
      new CodeBlock(),
      new HardBreak(),
      new Heading({ levels: [1, 2, 3] }),
      new Image(),
      new Italic(),
      new Link(),
      new ListItem(),
      new OrderedList(),
      new Strike(),
      new TodoItem(),
      new TodoList(),
      new Underline(),
    ],
  }),

  stageStart: {},
  stageTimer: {},
};

const actions = {
  async init({ dispatch, rootState }) {
    const stagedNotes = rootState.storageProvider.getStagedNotes();
    if (stagedNotes > 0) {
      // Changes have been staged, but haven't been committed
      // Ask the user what they want to do
      dispatch('modals/show', {
        component: RecoverChangesModal,
        properties: {
          notes: stagedNotes,
          allowDismiss: false,
        },
      }, {
        root: true,
      });
    }
  },

  async getNotes({
    commit, dispatch, state, rootState,
  }) {
    const notes = await rootState.storageProvider.getNotes(state.params);
    commit('setNotes', notes);
    dispatch('tags/getTags', null, { root: true });
  },

  async getNote({ commit, rootState }, { uuid }) {
    const note = await rootState.storageProvider.getNote(uuid);
    commit('setCurrentNote', note);
  },

  async deleteNote({ commit, dispatch, rootState }, { uuid }) {
    await rootState.storageProvider.deleteNote(uuid);
    await rootState.storageProvider.deleteNoteImages(uuid);
    commit('setCurrentNote', null);
    dispatch('getNotes');
  },

  async restoreDeleted({ dispatch, rootState }) {
    await rootState.storageProvider.restoreDeletedNotes();
    dispatch('getNotes');
  },

  async purgeDeleted({ commit, dispatch, rootState }) {
    const notes = await rootState.storageProvider.getNotes({
      limit: 0,
      deleted: true,
      uuidsOnly: true,
    });

    const imageDeletions = [];
    for (const note of notes) {
      imageDeletions.push(rootState.storageProvider.deleteNoteImages(note));
    }
    await rootState.storageProvider.purgeDeletedNotes();
    await Promise.all(imageDeletions);

    commit('setCurrentNote', null);
    dispatch('getNotes');
  },

  async newNote({ dispatch, rootState }) {
    const note = await rootState.storageProvider.createNote();
    dispatch('getNotes');
    return note.uuid;
  },

  async updateNote({
    commit, dispatch, state, rootState,
  }, { note }) {
    await rootState.storageProvider.updateNote(note);

    delete state.stageStart[note.uuid];
    delete state.stageTimer[note.uuid];
    rootState.storageProvider.deleteStagedNote(note.uuid);

    if (state.current && state.current.uuid === note.uuid) {
      commit('setCurrentNote', note);
    }

    dispatch('getNotes');
  },

  async deleteUnusedNoteImages({ dispatch, rootState }, { note }) {
    const dbImages = await dispatch(
      'images/getAll',
      { noteUuid: note.uuid, uuidsOnly: true },
      { root: true },
    );
    if (dbImages.length === 0) {
      return;
    }

    const noteImages = note.getLocalImageUuids();
    const deletedImages = dbImages.filter(dmImage => !noteImages.includes(dmImage));

    const imageDeletions = [];
    for (const image of deletedImages) {
      imageDeletions.push(rootState.storageProvider.deleteImage(image));
    }
    await Promise.all(imageDeletions);
  },

  async setTags({ dispatch }, { note, tags }) {
    note.tags = tags;
    dispatch('updateNote', { note });
  },

  async addTag({
    commit, dispatch, state, rootState,
  }, { note, tag }) {
    await rootState.storageProvider.createTag(tag);
    note.addTag(tag);
    await rootState.storageProvider.updateNote(note);

    if (state.current && state.current.uuid === note.uuid) {
      commit('setCurrentNote', note);
    }

    dispatch('getNotes');
    dispatch('tags/getTags', null, { root: true });
  },

  async removeTag({
    commit, dispatch, state, rootState,
  }, { note, tag }) {
    note.removeTag(tag);
    await rootState.storageProvider.updateNote(note);
    await rootState.storageProvider.deleteTag(tag);

    if (state.current && state.current.uuid === note.uuid) {
      commit('setCurrentNote', note);
    }

    dispatch('getNotes');
    dispatch('tags/getTags', null, { root: true });
  },

  async setArchived({ dispatch }, { note, archived }) {
    note.archived = archived;
    if (archived) {
      note.deleted = false;
    }
    dispatch('updateNote', { note });
  },

  async setDeleted({ dispatch }, { note, deleted }) {
    note.deleted = deleted;
    if (deleted) {
      note.archived = false;
    }
    dispatch('updateNote', { note });
  },

  discardUnsavedNotes({ rootState }) {
    rootState.storageProvider.clearStagedNotes();
  },

  async recoverUnsavedNotes({ dispatch, state, rootState }) {
    const stagedNotes = rootState.storageProvider.getStagedNotes();
    for (let i = 0; i < stagedNotes.length; i++) {
      dispatch('updateNote', {
        note: stagedNotes[i],
      });
      if (state.current.uuid === stagedNotes[i].uuid) {
        dispatch('getNote', {
          uuid: state.current.uuid,
        });
      }
    }
    rootState.storageProvider.clearStagedNotes();
  },

  async stageNote({ dispatch, state, rootState }, { content }) {
    // Don't spam the storage provider with save requests, cache the changes
    // in localStorage first.
    const note = state.current;

    note.updateContent(content);

    rootState.storageProvider.stageNote(note);

    clearTimeout(state.stageTimer[note.uuid]);

    const now = Date.now();

    if (!state.stageStart[note.uuid]) {
      state.stageStart[note.uuid] = now;
    }

    if (state.stageStart[note.uuid] < now - 5000) {
      // Save at least every five seconds
      state.stageStart[note.uuid] = now;
      dispatch('updateNote', {
        note,
      });
    } else {
      // Save half a second after the user stops typing
      state.stageTimer[note.uuid] = setTimeout(() => {
        dispatch('updateNote', {
          note,
        });
      }, 500);
    }
  },

  async getDataForBackup({ rootState }) {
    const notes = await rootState.storageProvider.getNotesRaw();
    const tags = await rootState.storageProvider.getTagsRaw();
    const images = await rootState.storageProvider.getImagesRaw();

    return { notes, tags, images };
  },

  async setDataFromBackup({ commit, dispatch, rootState }, { notes, tags, images }) {
    commit('setCurrentNote', null);
    await rootState.storageProvider.setNotesRaw(notes);
    await rootState.storageProvider.setTagsRaw(tags);
    await rootState.storageProvider.setImagesRaw(images);

    dispatch('getNotes');
  },

  async exportCurrentNote({ state }, { format, formatOptions }) {
    const exportProvider = EXPORT_FORMAT_MAP[format];

    const data = await exportProvider.exportNote({
      note: state.current,
      options: formatOptions,
      editor: state.editor,
      store: this,
    });

    const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${state.current.summary}.${exportProvider.EXTENSION}`);
  },
};

const mutations = {
  setNotes(state, notes) {
    state.notes = notes;
  },

  setCurrentNote(state, note) {
    state.current = note;
  },

  setParams(state, params) {
    state.params = params;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
